import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormGroup,
  FormGroupProps,
  FormLabel,
  SxProps,
  Theme,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import { flatten, uniq } from "lodash";
import { ReactNode } from "react";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";

import { ERROR_BOUNDARY_MESSAGE } from "@/constants";

type OptionValue = string | number | boolean;

export type FormCheckBoxGroupValue = OptionValue[];

interface Option extends Omit<FormControlLabelProps, "control" | "label" | "value"> {
  label: string;
  value: OptionValue | OptionValue[];
}

export type FormCheckBoxGroupProps<TFormValues extends FieldValues> = Omit<FormGroupProps, "defaultValue"> & {
  name: Path<TFormValues>;
  options: Option[];
  label?: ReactNode;
  disabled?: boolean;
  checkBoxSx?: SxProps<Theme>;
};

export function FormCheckBoxGroup<TFormValues extends FieldValues>({
  name,
  options = [],
  label = "",
  row = true,
  disabled = false,
  checkBoxSx,
  ...props
}: FormCheckBoxGroupProps<TFormValues>) {
  const { control, setValue } = useFormContext<TFormValues, FormCheckBoxGroupValue>();

  return (
    <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FormControl component="fieldset" variant="standard" disabled={disabled}>
            {label && <FormLabel component="legend">{label}</FormLabel>}

            <FormGroup row={row} {...props}>
              {options.map(({ label: optionLabel, value: optionValue, ...labelProps }) => {
                return (
                  <FormControlLabel
                    {...labelProps}
                    sx={{
                      ...labelProps.sx,
                      userSelect: "none",
                    }}
                    key={optionLabel}
                    label={optionLabel}
                    value={optionValue}
                    control={<Checkbox size="small" sx={{ ...checkBoxSx }} />}
                    checked={
                      Array.isArray(optionValue)
                        ? field.value.some((selected: OptionValue) => optionValue.includes(selected))
                        : field.value
                        ? field.value.includes(optionValue)
                        : false
                    }
                    onChange={() => {
                      setValue(
                        name,
                        field.value.some((item: string) =>
                          Array.isArray(optionValue) ? optionValue.includes(item) : item === optionValue
                        )
                          ? field.value.filter((item: string) =>
                              Array.isArray(optionValue) ? !optionValue.includes(item) : item !== optionValue
                            )
                          : uniq(flatten([...field.value, optionValue]))
                      );
                    }}
                  />
                );
              })}
            </FormGroup>
          </FormControl>
        )}
      />
    </Sentry.ErrorBoundary>
  );
}
